import type { ReactNode } from 'react';
import { Tip } from '../common/Tooltip';
import {
  PlayIcon,
  PauseIcon,
  BackwardIcon,
  ForwardIcon,
  MinusIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';

/**
 * Playback transport controls — shared by both dashboards' recordings player.
 * Fully presentational: renders the play/pause button, the jump cluster, and the
 * speed cluster, and reports intent through callbacks. State (isPlaying, rate,
 * seeking) lives in each page; this component only draws and dispatches.
 *
 * Layout keeps the three groups visually separate — a primary play button, then a
 * segmented jump pill (−30s / −10s / +10s / +30s), then a segmented speed pill
 * (slower / current rate / faster) — so the jump and fast-forward controls never
 * read as one undifferentiated row of buttons.
 */
interface PlaybackTransportProps {
  /** A stream is loaded (playbackUrl truthy). Gates the jump buttons and play-label. */
  hasStream: boolean;
  isPlaying: boolean;
  /** Disable the primary button (e.g. loading, or no segments in range). */
  playDisabled?: boolean;
  /** Play / pause / start-range — the page decides which based on hasStream. */
  onPlayPause: () => void;
  /** Seek the playhead by ±seconds within the continuous source. */
  onSkip: (deltaSec: number) => void;
  playbackRate: number;
  onSpeedUp: () => void;
  onSlowDown: () => void;
  /** Speed a single ↓ step lands on — used for the slow-down button's tooltip. */
  slowerSpeed?: number;
  className?: string;
}

const seg =
  'flex items-center justify-center px-2.5 py-1.5 text-[12px] font-mono text-foreground ' +
  'hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent';
/** The same pills at control-bar height — the Recorded wall's one-row bar. */
const segCompact =
  'flex h-7 items-center justify-center px-1.5 text-[11px] font-mono text-foreground ' +
  'hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent';
const pill = 'inline-flex shrink-0 items-center rounded-lg border border-border overflow-hidden divide-x divide-border';

/**
 * Jump cluster — segmented pill, reverse/forward by 10s and 30s.
 *
 * `side` draws just one half, so a host can put the halves either side of its play button;
 * `start` / `end` add the host's own segments at the pill's outer ends (the Recorded wall's
 * previous / next event).
 */
export function JumpPill({ onSkip, disabled = false, compact = false, side = 'both', start, end }: {
  onSkip: (deltaSec: number) => void;
  disabled?: boolean;
  compact?: boolean;
  side?: 'both' | 'back' | 'forward';
  start?: ReactNode;
  end?: ReactNode;
}) {
  const cls = compact ? segCompact : seg;
  const ic = compact ? 'h-3 w-3' : 'h-3.5 w-3.5';
  // A compact bar short of room drops the 30 s jumps first (Shift+←/→ still do them); see
  // PlaybackControls for the bar's tiers.
  const long = compact ? 'hidden group-data-[tier=wide]/bar:flex' : '';
  // Each button's label is a themed tooltip above it (Tip), not a browser `title`. A hidden
  // 30 s button hides with its tooltip wrapper, or the empty wrapper would still draw a divider.
  const jumpBtn = (d: number, label: string, key: string, hideCls: string, content: ReactNode) => (
    <Tip label={`${label} (${key})`} className={hideCls}>
      <button onClick={() => onSkip(d)} disabled={disabled} aria-label={label} className={cls}>{content}</button>
    </Tip>
  );
  return (
    <div className={pill}>
      {start}
      {side !== 'forward' && <>
        {jumpBtn(-30, 'Back 30 seconds', 'Shift+←', long, <><BackwardIcon className={`${ic} mr-1`} />30s</>)}
        {jumpBtn(-10, 'Back 10 seconds', '←', '', <><BackwardIcon className={`${ic} mr-1`} />10s</>)}
      </>}
      {side !== 'back' && <>
        {jumpBtn(10, 'Forward 10 seconds', '→', '', <>10s<ForwardIcon className={`${ic} ml-1`} /></>)}
        {jumpBtn(30, 'Forward 30 seconds', 'Shift+→', long, <>30s<ForwardIcon className={`${ic} ml-1`} /></>)}
      </>}
      {end}
    </div>
  );
}

/** Speed cluster — segmented pill: slower / current rate / faster. */
export function SpeedPill({ playbackRate, onSpeedUp, onSlowDown, slowerSpeed, fasterSpeed, compact = false }: {
  playbackRate: number;
  onSpeedUp: () => void;
  onSlowDown: () => void;
  slowerSpeed?: number;
  fasterSpeed?: number;
  compact?: boolean;
}) {
  const cls = compact ? segCompact : seg;
  const ic = compact ? 'h-3 w-3' : 'h-3.5 w-3.5';
  return (
    <div className={pill}>
      <Tip label={slowerSpeed != null ? `Slow down to ${slowerSpeed}×` : 'Slow down'}>
        <button onClick={onSlowDown} aria-label="Slow down" className={cls}>
          <MinusIcon className={ic} />
        </button>
      </Tip>
      <Tip label="Playback speed">
        <span className={`${compact ? 'flex h-7 items-center px-1.5 text-[12px]' : 'px-3 py-1.5 text-[13px]'} font-mono font-semibold text-foreground tabular-nums select-none`}>
          {playbackRate}×
        </span>
      </Tip>
      <Tip label={fasterSpeed != null ? `Speed up to ${fasterSpeed}×` : 'Speed up'}>
        <button onClick={onSpeedUp} aria-label="Speed up" className={cls}>
          <PlusIcon className={ic} />
        </button>
      </Tip>
    </div>
  );
}

export default function PlaybackTransport({
  hasStream,
  isPlaying,
  playDisabled = false,
  onPlayPause,
  onSkip,
  playbackRate,
  onSpeedUp,
  onSlowDown,
  slowerSpeed,
  className = '',
}: PlaybackTransportProps) {
  return (
    /* Wraps rather than overflows: the three clusters need ~440px of width, and the bar they
       sit in is only as wide as the player column — narrower than that on a split-screen
       window, a phone, or after an external display resizes the viewport. Unwrapped, the
       speed pill (and sometimes the jump pill) slid past the edge of a clipped parent and
       vanished. */
    <div className={`flex flex-wrap items-center gap-2 gap-y-2 sm:gap-3 ${className}`}>
      {/* Primary: play / pause / start range */}
      <button
        onClick={onPlayPause}
        disabled={playDisabled}
        className="flex shrink-0 items-center gap-1.5 rounded-lg bg-brand px-3.5 py-1.5 text-[13px] font-semibold text-white hover:bg-brand-dark transition-colors disabled:opacity-50">
        {isPlaying ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
        {hasStream ? (isPlaying ? 'Pause' : 'Resume') : 'Play Range'}
      </button>

      <JumpPill onSkip={onSkip} disabled={!hasStream} />
      <SpeedPill playbackRate={playbackRate} onSpeedUp={onSpeedUp} onSlowDown={onSlowDown} slowerSpeed={slowerSpeed} />
    </div>
  );
}
